import { Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useMediaQuery } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuditEntry, getAuditLog } from "../../service/LogService";
import { formatTime, orangeButton, outlinedButton, StyledTableCell, StyledTableRow, TableWrapper, toIso } from "./logStyles";

const emptyForm = { from: "", to: "", user: "", q: "" };

/** Admin changes: who, what, when (field names only) */
export default function AuditLogTable() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState(emptyForm);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const fetchAudit = useCallback(async () => {
    const data = await getAuditLog({ ...filters, from: toIso(filters.from), to: toIso(filters.to) }, page, isMobile, navigate);
    if (data) {
      setRows(data.results);
      setHasMore(data.hasMore);
    }
  }, [filters, page, isMobile, navigate]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const setField = (field: keyof typeof emptyForm) => (event: any) => setForm({ ...form, [field]: event.target.value });
  const apply = (values = form) => {
    setFilters(values);
    setPage(1);
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", marginTop: 1 }}>
        {t("logs.auditIntro")}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%", my: 1 }}>
        <TextField label={t("logs.from")} type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={form.from} onChange={setField("from")} />
        <TextField label={t("logs.to")} type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={form.to} onChange={setField("to")} />
        <TextField label={t("logs.admin")} size="small" value={form.user} onChange={setField("user")} />
        <TextField label={t("logs.search")} size="small" value={form.q} onChange={setField("q")} onKeyDown={(event) => event.key === "Enter" && apply()} />
        <Button variant="contained" onClick={() => apply()} sx={orangeButton}>
          {t("logs.filter")}
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setForm(emptyForm);
            apply(emptyForm);
          }}
          sx={outlinedButton}
        >
          {t("logs.clear")}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
        {t("logs.defaultRange")}
      </Typography>

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("logs.time")}</StyledTableCell>
              <StyledTableCell>{t("logs.actor")}</StyledTableCell>
              <StyledTableCell>{t("logs.action")}</StyledTableCell>
              <StyledTableCell>{t("logs.fields")}</StyledTableCell>
              <StyledTableCell>{t("logs.status")}</StyledTableCell>
              <StyledTableCell>{t("logs.reason")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row.id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTime(row.createdAt)}</TableCell>
                <TableCell>{row.actor?.email || row.actor?.userId}</TableCell>
                <TableCell sx={{ wordBreak: "break-word" }}>
                  {row.action}
                  {row.target && Object.keys(row.target).length ? ` (${Object.values(row.target).join(", ")})` : ""}
                </TableCell>
                <TableCell>{(row.fields || []).join(", ")}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.reason}</TableCell>
              </StyledTableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center" }}>
                  {t("logs.noEntries")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2 }}>
        <Button variant="outlined" disabled={page === 1} onClick={() => setPage(page - 1)} sx={outlinedButton}>
          {t("logs.previous")}
        </Button>
        <Typography>{t("logs.page", { page })}</Typography>
        <Button variant="outlined" disabled={!hasMore} onClick={() => setPage(page + 1)} sx={outlinedButton}>
          {t("logs.next")}
        </Button>
      </Box>
    </>
  );
}
